import React, { useCallback,useLayoutEffect, useEffect, useRef, useState } from 'react';
import { CalendarEvent, TouchGesture } from '../../types/calendar';
import { useCalendarState } from '../../hooks/useCalendarState';
import { useTouchGestures } from '../../hooks/useTouchGestures';
import CalendarHeader from '../../components/calendar/CalendarHeader';
import DayView from '../../components/calendar/DayView';
import WeekView from '../../components/calendar/WeekView';
import MonthView from '../../components/calendar/MonthView';
import EventModal from '../../components/calendar/EventModal';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/auth/hooks';
import { AppointmentDetails } from '../../components/appointments/DayView/components/AppointmentDetails';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';

// --- פונקציות עזר לבדיקה עם הפסקות ---
function parseHHMM(str?: string) {
  if (!str) return undefined;
  const [h, m] = str.split(':').map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return undefined;
  return h * 60 + m;
}
function rangeOverlap(s1: number, e1: number, s2: number, e2: number) {
  return Math.max(s1, s2) < Math.min(e1, e2);
}
function eventOverlapsBreaks(
  start: Date,
  end: Date,
  breaks: { startTime?: string; endTime?: string; start_time?: string; end_time?: string }[]
) {
  const s = start.getHours() * 60 + start.getMinutes();
  const e = end.getHours() * 60 + end.getMinutes();
  return breaks.some(b => {
    const bs = parseHHMM(b.startTime || b.start_time);
    const be = parseHHMM(b.endTime || b.end_time);
    return bs != null && be != null && rangeOverlap(s, e, bs, be);
  });
}

function CalendarPage() {
  const {
    currentDate,
    setCurrentDate,
    view,
    setView,
    events,
    setEvents,
    addEvent,
    updateEvent,
    dragState,
    setDragState,
    showEventModal,
    setShowEventModal,
    selectedTimeSlot,
    setSelectedTimeSlot,
    navigateDate
  } = useCalendarState();

  const { business, user, session, refreshBusiness } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {

  }, [business]);

  useEffect(() => {
    if (refreshBusiness) {
      refreshBusiness();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handler = () => {
      setCurrentDate(new Date());
      setView('day');
    };
    window.addEventListener('calendar-goto-today', handler);

    const dateHandler = (e: any) => {
      if (e.detail instanceof Date) {
        setCurrentDate(e.detail);
        setView('day');
      }
    };
    window.addEventListener('calendar-goto-date', dateHandler);

    return () => {
      window.removeEventListener('calendar-goto-today', handler);
      window.removeEventListener('calendar-goto-date', dateHandler);
    };
  }, [setCurrentDate, setView]);

  const [editingEvent, setEditingEvent] = useState<CalendarEvent | null>(null);
  const [selectedAppointment, setSelectedAppointment] = useState<Appointment | null>(null);

  const [selectedStaffId, setSelectedStaffId] = useState<string | null>(null);
  const [staffList, setStaffList] = useState<{ id: string; name: string }[]>([]);
  const [businessHours, setBusinessHours] = useState<{
    start_time: string;
    end_time: string;
    regular_hours?: any;
    special_dates?: any[];
  } | null>(null);
  const [businessHoursLoading, setBusinessHoursLoading] = useState(false);

  const [staffHoursMap, setStaffHoursMap] = useState<{ [staffId: string]: any }>({});

  useEffect(() => {
    if (!user) return;
    if (!business || !business.id) {

      return;
    }

  const fetchStaff = async () => {
    const { data, error } = await supabase
      .from('users')
      .select('id, metadata, name, email, role, business_id')
      .eq('business_id', business.id)
      .in('role', ['staff', 'admin']); // הוסף פה עוד תפקידים אם יש

    if (error) {

      setStaffList([]);
      return;
    }
    if (!data?.length) {

      setStaffList([]);
      return;
    }

    // מנהלים (role !== 'staff') מקדימה
    const sorted = [...data].sort((a, b) => {
      const aMgr = (a.role ?? '').toLowerCase() !== 'staff';
      const bMgr = (b.role ?? '').toLowerCase() !== 'staff';
      return aMgr === bMgr ? 0 : aMgr ? -1 : 1;
    });

    const list = sorted.map((u: any) => ({
      id: u.id,
      name: u.metadata?.name || u.name || u.email || 'ללא שם',
    }));
    setStaffList(list);

    // ברירת מחדל: אם אין בחירה – בחר מנהל ראשון
    const firstManager = sorted.find(
      (u: any) => (u.role ?? '').toLowerCase() !== 'staff'
    );
    if (!selectedStaffId && firstManager) {
      setSelectedStaffId(firstManager.id);
    } else if (
      selectedStaffId &&
      !sorted.some((u: any) => u.id === selectedStaffId)
    ) {
      // אם הבחירה הקודמת לא קיימת יותר – נפולבק למנהל
      if (firstManager) setSelectedStaffId(firstManager.id);
    }
  };

  fetchStaff();
  // חשוב: אנחנו משתמשים ב-selectedStaffId כדי להחליט אם להגדיר ברירת מחדל
}, [business?.id, selectedStaffId]);

  const [specialDates, setSpecialDates] = useState<any[]>([]);

  useEffect(() => {
    if (!business || !business.id) {
      setBusinessHours(null);
      setSpecialDates([]);
      return;
    }

    setBusinessHoursLoading(true);

    const fetchBusinessHours = async () => {
      try {
        const { data, error } = await supabase
          .from('business_hours')
          .select('regular_hours, special_dates')
          .eq('business_id', business.id)
          .single();

        if (error || !data) {
          setBusinessHours(null);
          setSpecialDates([]);
          setBusinessHoursLoading(false);
          return;
        }

        const regularHours = typeof data.regular_hours === 'string'
          ? JSON.parse(data.regular_hours)
          : data.regular_hours;

        setSpecialDates(Array.isArray(data.special_dates) ? data.special_dates : []);

        const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
        const currentDayName = dayNames[currentDate.getDay()];
        const todayHours = regularHours && regularHours[currentDayName];

        const special = Array.isArray(data.special_dates)
          ? data.special_dates.find((s: any) => s.date === currentDate.toISOString().split('T')[0])
          : null;

        // שמור גם regular_hours ו־special_dates
        if (special?.is_closed) {
          setBusinessHours({
            start_time: '',
            end_time: '',
            regular_hours: regularHours,
            special_dates: data.special_dates
          });
        } else if ((special?.start_time || special?.startTime) && (special?.end_time || special?.endTime)) {
          setBusinessHours({
            start_time: special.start_time || special.startTime,
            end_time: special.end_time || special.endTime,
            regular_hours: regularHours,
            special_dates: data.special_dates
          });
        } else if (todayHours && (todayHours.is_active || todayHours.isActive)) {
          setBusinessHours({
            start_time: todayHours.start_time || todayHours.startTime,
            end_time: todayHours.end_time || todayHours.endTime,
            regular_hours: regularHours,
            special_dates: data.special_dates
          });
        } else {
          setBusinessHours({
            start_time: '',
            end_time: '',
            regular_hours: regularHours,
            special_dates: data.special_dates
          });
        }
      } finally {
        setBusinessHoursLoading(false);
      }
    };

    fetchBusinessHours();
  }, [business, currentDate]);

  const fetchAppointmentsFromDB = async () => {
    if (!business || !business.id) return [];
    const { data, error } = await supabase
      .from('appointments')
      .select(`
        id,
        start_time,
        end_time,
        staff_id,
        status,
        business_id,
        customer:customer_id (
          id,
          name,
          phone
        )
      `)
      .eq('business_id', business.id);

    if (error) {

      return [];
    }

    return data
      .filter((a: any) => !selectedStaffId || a.staff_id === selectedStaffId)
      .map((a: any) => {
        let color = '';
        let statusLabel = '';
        switch (a.status) {
          case 'booked':
            statusLabel = 'ממתין לאישור';
            color = '#3B82F6';
            break;
          case 'confirmed':
            statusLabel = 'מאושר';
            color = '#10B981';
            break;
          case 'completed':
            statusLabel = 'הושלם';
            color = '#6366F1';
            break;
          case 'canceled':
            statusLabel = 'בוטל';
            color = '#EF4444';
            break;
          case 'no_show':
            statusLabel = 'לא הגיע';
            color = '#F59E0B';
            break;
          default:
            statusLabel = 'לא ידוע';
            color = '#9CA3AF';
        }

        return {
          id: a.id,
          title:
            a.customer?.name && a.customer?.phone
              ? `${a.customer.name} - ${a.customer.phone} - ${statusLabel}`
              : `ללא כותרת - ${statusLabel}`,
          startTime: new Date(a.start_time),
          endTime: new Date(a.end_time),
          staffId: a.staff_id,
          color
        };
      });
  };

  const firstEventRef = useRef<HTMLDivElement | null>(null);
  const dayColumnRef = useRef<HTMLDivElement | null>(null);
  const scrollWrapRef = useRef<HTMLDivElement | null>(null);

useLayoutEffect(() => {
  if (view === 'day' && dayColumnRef.current) {
    setTimeout(() => {
      if (events && events.length > 0 && firstEventRef.current) {
        firstEventRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } else {
        dayColumnRef.current.scrollTo({
          top: dayColumnRef.current.scrollHeight / 2,
          behavior: 'smooth'
        });
      }
    }, 100);
  }
}, [events, view, currentDate]);

  useLayoutEffect(() => {
    const el = scrollWrapRef.current;
    if (!el) return;

    const applyPadding = () => {
      const rect = el.getBoundingClientRect();
      const spaceToBottom = window.innerHeight - rect.bottom;
      const needed = Math.max(0, -spaceToBottom) + 12;
      el.style.paddingBottom = `${needed}px`;
    };

    applyPadding();
    const ro = new ResizeObserver(applyPadding);
    ro.observe(el);
    window.addEventListener('resize', applyPadding);

    const t = setTimeout(applyPadding, 0);

    return () => {
      clearTimeout(t);
      window.removeEventListener('resize', applyPadding);
      ro.disconnect();
    };
  }, []);

  useEffect(() => {
    if (!business || !business.id) {
      setEvents([]);
      return;
    }

    const loadEvents = async () => {
      const realEvents = await fetchAppointmentsFromDB();
      setEvents(realEvents);
    };

    loadEvents();
  }, [business, currentDate, selectedStaffId]);

  const handleGesture = useCallback((gesture: TouchGesture) => {
    switch (gesture.type) {
      case 'swipe':
        gesture.direction === 'left' ? navigateDate('next') : navigateDate('prev');
        break;
      case 'doubletap':
        setSelectedTimeSlot(new Date());
        setShowEventModal(true);
        break;
    }
  }, [navigateDate]);

  const { handleTouchStart, handleTouchEnd } = useTouchGestures({
    onGesture: handleGesture,
    threshold: 50
  });

  const handleDragStart = useCallback((event: CalendarEvent, position: { x: number; y: number }) => {
    setDragState(prev => ({
      ...prev,
      isDragging: true,
      draggedEvent: event,
      startPosition: position,
      currentPosition: position,
      originalTime: event.startTime
    }));
  }, [setDragState]);

  const [dragPreviewEvent, setDragPreviewEvent] = useState<CalendarEvent | null>(null);

  const handleDragMove = useCallback((position: { x: number; y: number }) => {
    if (dragState.isDragging && dragState.draggedEvent) {
      setDragState(prev => ({
        ...prev,
        currentPosition: position
      }));

      const timeDiff = position.y - dragState.startPosition.y;
      const minutesDiff = Math.round(timeDiff / (80 / 60));
      const newStartTime = snapToFiveMinutes(new Date(dragState.originalTime.getTime() + minutesDiff * 60000));
      const duration = dragState.draggedEvent.endTime.getTime() - dragState.draggedEvent.startTime.getTime();
      const newEndTime = new Date(newStartTime.getTime() + duration);

      setDragPreviewEvent({
        ...dragState.draggedEvent,
        startTime: newStartTime,
        endTime: newEndTime
      });
    }
  }, [dragState.isDragging, dragState.draggedEvent, dragState.startPosition.y, dragState.originalTime, setDragState]);

  const snapToFiveMinutes = (date: Date): Date => {
    const snapped = new Date(date);
    const minutes = snapped.getMinutes();
    const remainder = minutes % 5;
    snapped.setMinutes(minutes - remainder + (remainder >= 3 ? 5 : 0));
    snapped.setSeconds(0, 0);
    return snapped;
  };

  const handleDragEnd = useCallback(() => {
    if (dragState.isDragging && dragState.draggedEvent) {
      const timeDiff = dragState.currentPosition.y - dragState.startPosition.y;
      const minutesDiff = Math.round(timeDiff / (80 / 60));
      const newStartTime = snapToFiveMinutes(new Date(dragState.originalTime.getTime() + minutesDiff * 60000));
      const duration = dragState.draggedEvent.endTime.getTime() - dragState.draggedEvent.startTime.getTime();
      const newEndTime = new Date(newStartTime.getTime() + duration);

      const dayBreaks = getDayBreaks();

      // 🔒 א. חסימה על הפסקות
      if (eventOverlapsBreaks(newStartTime, newEndTime, dayBreaks)) {
        toast.error('אי אפשר למקם תור בתוך זמן הפסקה', {
          style: { background: '#fff', color: '#b45309', fontWeight: 700, border: '1px solid #fcd34d' },
          icon: '☕'
        });
        setDragState({ isDragging: false, draggedEvent: null, startPosition: {x:0,y:0}, currentPosition: {x:0,y:0}, originalTime: new Date() });
        setDragPreviewEvent(null);
        return;
      }

      // 🔒 ב. חסימה על שעות פעילות העסק
      if (!isInBusinessHours(newStartTime) || !isInBusinessHours(newEndTime)) {
        toast.error('לא ניתן לגרור תור אל מחוץ לשעות הפעילות של העסק', {
          style: {
            background: '#fff',
            color: '#b91c1c',
            fontWeight: 600,
            fontSize: 16,
            border: '1px solid #fca5a5',
            boxShadow: '0 2px 8px #fca5a540',
            direction: 'rtl'
          },
          icon: '⏰'
        });
        setDragState({ isDragging: false, draggedEvent: null, startPosition: {x:0,y:0}, currentPosition: {x:0,y:0}, originalTime: new Date() });
        setDragPreviewEvent(null);
        return;
      }

      updateEvent(dragState.draggedEvent.id, { startTime: newStartTime, endTime: newEndTime });
      (async () => {
        const { error } = await supabase
          .from('appointments')
          .update({ start_time: newStartTime.toISOString(), end_time: newEndTime.toISOString() })
          .eq('id', dragState.draggedEvent.id);
        if (!error) fetchAppointmentsFromDB().then(setEvents);
      })();
    }

    setDragPreviewEvent(null);
    setDragState({ isDragging: false, draggedEvent: null, startPosition: {x:0,y:0}, currentPosition: {x:0,y:0}, originalTime: new Date() });
  }, [dragState, updateEvent, setDragState, fetchAppointmentsFromDB, setEvents]);

  const handleTimeSlotDoubleClick = useCallback((date: Date) => {
    setSelectedTimeSlot(snapToFiveMinutes(date));
    setEditingEvent(null);
    setShowEventModal(true);
  }, []);

  const handleEventClick = useCallback((event: CalendarEvent) => {
    const fetchAppointmentDetails = async () => {
      const { data, error } = await supabase
        .from('appointments')
        .select(`
          *,
          customer:customer_id (
            id, name, phone
          ),
          users:staff_id (
            id, name
          ),
          services:service_id (
            id, name_he
          )
        `)
        .eq('id', event.id)
        .single();

      if (error || !data) {

        return;
      }

      setSelectedAppointment(data);
    };

    fetchAppointmentDetails();
  }, []);

  const handleAddEvent = useCallback(() => {
    setSelectedTimeSlot(snapToFiveMinutes(new Date()));
    setEditingEvent(null);
    setShowEventModal(true);
  }, [setSelectedTimeSlot, setEditingEvent, setShowEventModal]);

  const handleEventSave = useCallback((eventData: Omit<CalendarEvent, 'id'>) => {
    const snappedEventData = {
      ...eventData,
      startTime: snapToFiveMinutes(eventData.startTime),
      endTime: snapToFiveMinutes(eventData.endTime)
    };

    const dayBreaks = getDayBreaks();
    if (eventOverlapsBreaks(snappedEventData.startTime, snappedEventData.endTime, dayBreaks)) {
      toast.error('אי אפשר לקבוע תור בתוך זמן הפסקה', {
        style: { background: '#fff', color: '#b45309', fontWeight: 700, border: '1px solid #fcd34d' },
        icon: '☕'
      });
      return;
    }

    if (editingEvent) {
      updateEvent(editingEvent.id, snappedEventData);
    } else {
      addEvent(snappedEventData);
    }
    setShowEventModal(false);
    setSelectedTimeSlot(null);
    setEditingEvent(null);
  }, [addEvent, updateEvent, editingEvent, setShowEventModal, setSelectedTimeSlot, setEditingEvent]);

  const handleAppointmentStatusChange = useCallback(() => {
    setSelectedAppointment(null);
  }, []);

  const handleAppointmentUpdate = useCallback(() => {
    setSelectedAppointment(null);
    fetchAppointmentsFromDB().then(setEvents);
  }, [fetchAppointmentsFromDB, setEvents]);

  const handleDateSelect = useCallback((date: Date) => {
    setCurrentDate(date);
    setView('day');
  }, [setCurrentDate, setView]);

  const realRole =
    (user?.user_metadata && user.user_metadata.role) ||
    user?.role ||
    (user?.user_metadata && user.user_metadata['role']);
  const isAdmin =
    realRole === 'admin' ||
    realRole === 'ADMIN' ||
    realRole === 'Admin';

  useEffect(() => {

  }, [user, isAdmin, realRole]);

  const handleDeleteAppointment = useCallback(async (id: string) => {
    const { error } = await supabase
      .from('appointments')
      .delete()
      .eq('id', id);

    if (error) {
      toast.error('שגיאה במחיקת התור');
      return;
    }

    fetchAppointmentsFromDB().then(setEvents);
  }, [fetchAppointmentsFromDB, setEvents]);

  useEffect(() => {
    if (staffList.length === 0) return;

    const fetchStaffHours = async () => {
      const { data, error } = await supabase
        .from('staff_hours')
        .select('staff_id, regular_hours, special_dates');

      if (error || !data) {

        return;
      }

      const map: { [staffId: string]: any } = {};
      for (const staff of staffList) {
        const staffRow = data.find((row: any) => row.staff_id === staff.id);
        if (staffRow) {
          map[staff.id] = {
            regular_hours: typeof staffRow.regular_hours === 'string'
              ? JSON.parse(staffRow.regular_hours)
              : staffRow.regular_hours,
            special_dates: staffRow.special_dates
          };
        }
      }
      setStaffHoursMap(map);
    };

    fetchStaffHours();
  }, [staffList]);

  const handleAddBreak = async (startHour: number) => {
    const start = `${startHour.toString().padStart(2, '0')}:00`;
    const end = `${(startHour + 1).toString().padStart(2, '0')}:00`;
    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const currentDayName = dayNames[currentDate.getDay()];

    if (!businessHours || !businessHours.regular_hours) {
      toast.error('שעות פעילות לא נטענו עדיין');
      return;
    }
    const regularHours = businessHours.regular_hours;
    if (!regularHours[currentDayName]) {
      toast.error('לא מוגדרות שעות פעילות ליום זה');
      return;
    }

    if (!regularHours[currentDayName].breaks) {
      regularHours[currentDayName].breaks = [];
    }

    // מניעת כפילות הפסקות
    const exists = regularHours[currentDayName].breaks.some(
      (b: any) => b.startTime === start && b.endTime === end
    );
    if (exists) {
      toast.error('הפסקה זו כבר קיימת');
      return;
    }

    regularHours[currentDayName].breaks.push({ startTime: start, endTime: end });

    const { error } = await supabase
      .from('business_hours')
      .update({ regular_hours: regularHours })
      .eq('business_id', business.id);

    if (error) {
      toast.error('שגיאה בשמירת הפסקה');
      return;
    }

    if (typeof refreshBusiness === 'function') {
      refreshBusiness();
    }
  };

  const handleRemoveBreak = async (startTime: string, endTime: string) => {
    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const currentDayName = dayNames[currentDate.getDay()];

    if (!businessHours || !businessHours.regular_hours) {
      toast.error('שעות פעילות לא נטענו עדיין');
      return;
    }
    const regularHours = businessHours.regular_hours;
    if (!regularHours[currentDayName] || !Array.isArray(regularHours[currentDayName].breaks)) {
      toast.error('אין הפסקות ליום זה');
      return;
    }

    // הסר break לפי startTime ו-endTime
    regularHours[currentDayName].breaks = regularHours[currentDayName].breaks.filter(
      (b: any) => !(b.startTime === startTime && b.endTime === endTime)
    );

    const { error } = await supabase
      .from('business_hours')
      .update({ regular_hours: regularHours })
      .eq('business_id', business.id);

    if (error) {
      toast.error('שגיאה במחיקת הפסקה');
      return;
    }

    if (typeof refreshBusiness === 'function') {
      refreshBusiness();
    }
  };

  function getStaffDayHours() {
    // If admin and staff selected and staffHoursMap has entry
    if (isAdmin && selectedStaffId && staffHoursMap[selectedStaffId]) {
      const staffHours = staffHoursMap[selectedStaffId];
      const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
      const currentDayName = dayNames[currentDate.getDay()];
      const todayHours = staffHours.regular_hours?.[currentDayName];
      const todayDateStr = currentDate.toISOString().split('T')[0];
      const special = Array.isArray(staffHours.special_dates)
        ? staffHours.special_dates.find((s: any) => s.date === todayDateStr)
        : null;
      if (special) {
        return {
          start_time: special.start_time || special.startTime || '',
          end_time: special.end_time || special.endTime || '',
          is_special: true,
          special_note: special.note || '',
          is_closed: !!special.is_closed,
        };
      }
      if (
        todayHours &&
        (todayHours.is_active || todayHours.isActive) &&
        (todayHours.start_time || todayHours.startTime) &&
        (todayHours.end_time || todayHours.endTime)
      ) {
        return {
          start_time: todayHours.start_time || todayHours.startTime || '',
          end_time: todayHours.end_time || todayHours.endTime || '',
          is_special: false,
          is_closed: false
        };
      }
      // If staff exists but no hours, fallback to business hours
    }
    // Fallback to business hours
    if (businessHours) {
      // Check for special date
      if (Array.isArray(businessHours.special_dates)) {
        const todayDateStr = currentDate.toISOString().split('T')[0];
        const special = businessHours.special_dates.find((s: any) => s.date === todayDateStr);
        if (special) {
          return {
            start_time: special.start_time || special.startTime || '',
            end_time: special.end_time || special.endTime || '',
            is_special: true,
            special_note: special.note || '',
            is_closed: !!special.is_closed,
          };
        }
      }
      // Regular business hours
      if (businessHours.start_time || businessHours.startTime || businessHours.end_time || businessHours.endTime) {
        return {
          start_time: businessHours.start_time || businessHours.startTime || '',
          end_time: businessHours.end_time || businessHours.endTime || '',
          is_special: false,
          is_closed: false
        };
      }
    }
    // If no hours at all, mark as closed
    return {
      start_time: '',
      end_time: '',
      is_special: false,
      is_closed: true
    };
  }

  // חישוב breaks ליום הנוכחי
  function getDayBreaks() {
    let breaks: { startTime: string; endTime: string }[] = [];
    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const currentDayName = dayNames[currentDate.getDay()];

    // Prefer staff breaks if available
    if (isAdmin && selectedStaffId && staffHoursMap[selectedStaffId]) {
      const staffHours = staffHoursMap[selectedStaffId];
      const todayHours = staffHours.regular_hours?.[currentDayName];
      if (todayHours && Array.isArray(todayHours.breaks)) {
        breaks = todayHours.breaks.map((b: any) => ({
          startTime: b.startTime || b.start_time,
          endTime: b.endTime || b.end_time
        }));
      }
    }

    // Fallback to business breaks if staff breaks not available
    if (
      breaks.length === 0 &&
      businessHours &&
      businessHours.regular_hours &&
      businessHours.regular_hours[currentDayName] &&
      Array.isArray(businessHours.regular_hours[currentDayName].breaks)
    ) {
      breaks = businessHours.regular_hours[currentDayName].breaks.map((b: any) => ({
        startTime: b.startTime || b.start_time,
        endTime: b.endTime || b.end_time
      }));
    }

    return breaks;
  }

  const renderCalendarView = () => {
    if (business && business.id && businessHoursLoading) {
      // הסר את LoadingScreen, תציג הודעה פשוטה או כלום
      return <div>טוען שעות פעילות...</div>;
    }

    let filteredEvents = events;
    if (isAdmin && selectedStaffId) {
      filteredEvents = events.filter((e: any) => e.staffId === selectedStaffId);
    } else if (!isAdmin && user?.id) {
      filteredEvents = events.filter((e: any) => e.staffId === user.id);
    }

    const staffBusinessHours = getStaffDayHours();
    const dayBreaks = getDayBreaks();

    const commonProps = {
      currentDate,
      events: filteredEvents,
      dragState,
      onDragStart: handleDragStart,
      onDragMove: handleDragMove,
      onDragEnd: handleDragEnd,
      onTimeSlotDoubleClick: handleTimeSlotDoubleClick,
      onEventClick: handleEventClick,
      dragPreviewEvent,
      businessOpenTime: staffBusinessHours?.start_time || '',
      businessCloseTime: staffBusinessHours?.end_time || '',
      firstEventRef,
      onDeleteAppointment: handleDeleteAppointment,
      isSpecialDay: staffBusinessHours?.is_special || false,
      specialNote: staffBusinessHours?.special_note || '',
      isClosed: staffBusinessHours?.is_closed || false,
      breaks: getDayBreaks(), // always pass breaks array
      onAddBreak: handleAddBreak, // ודא שזה עובר ל-DayView
      onRemoveBreak: handleRemoveBreak, // חדש
    };

    switch (view) {
      case 'day':
        return (
          <DayView
            {...commonProps}
          />
        );
      case 'week':
        return <WeekView {...commonProps} />;
      case 'month':
        return <MonthView currentDate={currentDate} events={filteredEvents} onDateSelect={handleDateSelect} onDeleteAppointment={handleDeleteAppointment} />;
      default:
        return <WeekView {...commonProps} />;
    }
  };

  const isInBusinessHours = (date: Date) => {
    if (!businessHours?.start_time && !businessHours?.startTime) return false;
    if (!businessHours?.end_time && !businessHours?.endTime) return false;
    const [openH, openM] = (businessHours.start_time || businessHours.startTime).split(':').map(Number);
    const [closeH, closeM] = (businessHours.end_time || businessHours.endTime).split(':').map(Number);
    const openMinutes = openH * 60 + openM;
    const closeMinutes = closeH * 60 + closeM;
    const minutes = date.getHours() * 60 + date.getMinutes();
    return minutes >= openMinutes && minutes < closeMinutes;
  };

  const BOTTOM_INSET = 50; // עדכן לגובה המדויק של ה-bottom nav שלך

  return (
    <div className="fixed inset-0 w-full h-full bg-gradient-to-br from-[#f0f6ff] to-[#e0e7ff] overflow-hidden">
      <div className="fixed top-0 left-0 right-0 z-50">
        <CalendarHeader
          currentDate={currentDate}
          view={view}
          onViewChange={setView}
          onNavigate={navigateDate}
          onAddEvent={handleAddEvent}
          staffList={isAdmin ? staffList : []}
          selectedStaffId={isAdmin ? selectedStaffId : undefined}
          onStaffSelect={isAdmin ? setSelectedStaffId : undefined}
          currentUser={user}
          specialDates={specialDates}
        />
      </div>
      <div
        className="absolute left-0 right-0"
        style={{ top: 166, bottom: BOTTOM_INSET }}
      >
        <div
          className="w-full h-full"
          style={{
            height: '100%',
            overflowY: 'auto',
            WebkitOverflowScrolling: 'touch',
            paddingBottom: BOTTOM_INSET, // שלא יסתתר מאחורי ה-bottom nav
          }}
          ref={(el) => {
            dayColumnRef.current = el;
            scrollWrapRef.current = el;
          }}
        >
          <div className="pt-4 px-0" style={{ minHeight: '100%' }}>
            {renderCalendarView()}
          </div>
        </div>
      </div>
      {selectedAppointment && (
        <AppointmentDetails
          appointment={selectedAppointment}
          onClose={() => setSelectedAppointment(null)}
          onUpdate={handleAppointmentUpdate}
        />
      )}
      <EventModal
        isOpen={showEventModal}
        onClose={() => {
          setShowEventModal(false);
          setSelectedTimeSlot(null);
          setEditingEvent(null);
        }}
        onSave={handleEventSave}
        initialDate={selectedTimeSlot || undefined}
        editEvent={editingEvent || undefined}
      />
    </div>
  );
}

export default CalendarPage;